import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import researchReportJobsApi, {
  ResearchReportJob,
  ResearchReportJobStatus,
} from "../apis/researchReportJobsApi";
import { getApiErrorMessage } from "../utils/apiErrorMessage";

type StatusFilter = "" | ResearchReportJobStatus;

const statusOptions: Array<{ value: StatusFilter; label: string }> = [
  { value: "", label: "All statuses" },
  { value: "queued", label: "Queued" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

const statusColor: Record<ResearchReportJobStatus, "default" | "warning" | "success" | "error" | "info"> = {
  queued: "default",
  running: "warning",
  completed: "success",
  failed: "error",
};

function toDateInput(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
}

/**
 * Deep research reports page:
 * - filter bar (date/user/status/title/job/thread)
 * - report jobs table
 * - open deep research thread + open completed report link; failed job reason on status chip tooltip
 */
const ResearchReports: React.FC = () => {
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 14);
    return toDateInput(d);
  }, [today]);
  const defaultEnd = useMemo(() => toDateInput(today), [today]);

  const [jobs, setJobs] = useState<ResearchReportJob[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingRequestedBy, setLoadingRequestedBy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [requestedBy, setRequestedBy] = useState("");
  const [requestedByOptions, setRequestedByOptions] = useState<string[]>([]);
  const [status, setStatus] = useState<StatusFilter>("");

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await researchReportJobsApi.listJobs({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        requested_by: requestedBy.trim() || undefined,
        status: status || undefined,
        limit: 100,
        offset: 0,
      });
      setJobs(Array.isArray(response.jobs) ? response.jobs : []);
      setTotal(Number(response.total || 0));
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load research report jobs"));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, requestedBy, status]);

  const loadRequestedByOptions = useCallback(async () => {
    try {
      setLoadingRequestedBy(true);
      const options = await researchReportJobsApi.listRequestedByOptions();
      setRequestedByOptions(options);
    } catch {
      setRequestedByOptions([]);
    } finally {
      setLoadingRequestedBy(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Deep Research Reports - Theo";
    loadRequestedByOptions();
    fetchJobs();
    return () => {
      document.title = "Theo";
    };
  }, [fetchJobs, loadRequestedByOptions]);

  const handleResetFilters = () => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setRequestedBy("");
    setStatus("");
  };

  return (
    <Box sx={{ p: 3, width: "100%", bgcolor: "#F8F9FA" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Deep Research Reports
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Start Date" type="date" fullWidth value={startDate} onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="End Date" type="date" fullWidth value={endDate} onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LayeredAutocompleteSelect
                label="Requested By"
                fullWidth
                value={requestedBy}
                onChange={value => setRequestedBy(value)}
                disabled={loadingRequestedBy}
                disableClearable
                options={[{ value: "", label: "All users" }, ...requestedByOptions.map(value => ({ value, label: value }))]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select label="Status" fullWidth value={status} onChange={(event) => setStatus(event.target.value as StatusFilter)} InputLabelProps={{ shrink: true }}>
                {statusOptions.map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", gap: 1.25, mt: 2 }}>
            <Button variant="contained" onClick={fetchJobs} disabled={loading}>
              {loading ? "Loading..." : "Apply Filters"}
            </Button>
            <Button variant="outlined" onClick={handleResetFilters} disabled={loading}>
              Reset
            </Button>
            <Button variant="text" onClick={fetchJobs} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {jobs.length} of {total} jobs
          </Typography>
        </CardContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Report Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Report</TableCell>
                <TableCell>Thread</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography sx={{ py: 1.5 }} color="text.secondary">
                      No research report jobs found for these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                jobs.map((job) => {
                  const threadPath = `/deep-research/${job.ai_thread_id}`;
                  const canOpenReport =
                    job.status === "completed" &&
                    typeof job.report_url === "string" &&
                    job.report_url.trim().length > 0;
                  return (
                    <TableRow key={job.job_id} hover>
                      <TableCell>{job.job_id}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap title={job.report_title || job.request_text}>
                          {job.report_title || job.request_text || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {job.status === "running" ? (
                          <Box sx={{ minWidth: 140 }}>
                            <Box sx={{ position: "relative" }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.max(0, Math.min(100, Number(job.progress_percent || 0)))}
                                sx={{
                                  height: 20,
                                  borderRadius: 1,
                                  backgroundColor: "#E5E7EB",
                                  "& .MuiLinearProgress-bar": { backgroundColor: "#1976d2" },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 700,
                                  color: "#FFFFFF",
                                  textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                                }}
                              >
                                {Math.round(Number(job.progress_percent || 0))}%
                              </Typography>
                            </Box>
                          </Box>
                        ) : job.status === "failed" ? (
                          <Tooltip
                            title={
                              String(job.client_error_message || "").trim() ||
                              "The report failed. Please retry."
                            }
                            arrow
                            placement="top"
                          >
                            <span>
                              <Chip
                                size="small"
                                label={job.status}
                                color={statusColor[job.status] || "default"}
                                variant="outlined"
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Chip size="small" label={job.status} color={statusColor[job.status] || "default"} variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{job.requested_by || "-"}</TableCell>
                      <TableCell>{formatDate(job.created_at)}</TableCell>
                      <TableCell>{formatDate(job.completed_at)}</TableCell>
                      <TableCell>
                        {canOpenReport ? (
                          <Link href={job.report_url as string} target="_blank" rel="noreferrer" underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            Open <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Link
                          href={threadPath}
                          target="_blank"
                          rel="noreferrer"
                          underline="hover"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                        >
                          {job.ai_thread_id} <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default ResearchReports;
